import { Panel } from '@/components/erp/primitives'
import { BucketAxis, ChartFrame, RankedBars, Sparkbars } from '@/shared/ui/charts'
import { fmtNumber, humanise } from '@/shared/lib/format'
import type { TransferStatisticsResponse } from '@/shared/api/types'
import {
  INTAKE_CHANNEL_LABEL, REGISTRATION_REASON_CODE, STATUS_TONE, TRANSFER_REASON_LABEL,
  TRANSFER_STATUS_LABEL,
} from './api'

/**
 * The queue, in four pictures.
 *
 * ## What each one is for
 *
 * **How long things have been waiting** comes first because it is the only chart here
 * anyone *acts* on. "Nine requests have sat in their current state for over a fortnight"
 * is a morning's work; "forty requests are UNDER_REVIEW" is not, because it says nothing
 * about whether that is normal. It is also not derivable from the status breakdown, which
 * is why the backend computes it.
 *
 * **Raised against recorded** is the drive's pulse: what is coming in versus what the
 * IEBC has confirmed. They are two panels sharing an x domain rather than two coloured
 * series in one, because the second is a fraction of the first and a shared scale would
 * flatten it to nothing.
 *
 * **Where requests are** and **why they were raised** are ranked bars, not pie slices.
 * Fifteen statuses in a pie is fifteen wedges nobody can compare, and the reader's actual
 * question — "which is biggest, and by how much" — is what length answers and angle does
 * not.
 *
 * ## Why nothing here is colour-coded by category
 *
 * The chart tokens are warm and desaturated by design; run through a CVD check, adjacent
 * pairs fail, so hue cannot carry identity. Everything encodes with length and position,
 * and state gets a labelled status dot beside the *text*. See `shared/ui/charts`.
 *
 * ## Degrading
 *
 * `openAgeBuckets`, `raisedDaily` and `byKind` arrive with prompt 28. Against an older
 * backend they are absent and their panels simply do not render — an empty chart frame
 * that says "no data" would be a lie about the data rather than about the deployment.
 */
export function TransferCharts({ stats }: { stats: TransferStatisticsResponse | null }) {
  if (!stats) return null

  const byStatus = Object.entries(stats.byStatus ?? {})
    .filter(([, n]) => n > 0)
    .sort((a, b) => b[1] - a[1])
    .map(([code, value]) => ({ label: TRANSFER_STATUS_LABEL[code] ?? humanise(code), value }))

  const byReason = Object.entries(stats.byReason ?? {})
    .filter(([, n]) => n > 0)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 6)
    .map(([code, value]) => ({
      label: code === REGISTRATION_REASON_CODE
        ? 'Not yet registered'
        : TRANSFER_REASON_LABEL[code] ?? humanise(code),
      value,
    }))

  const byChannel = Object.entries(stats.byChannel ?? {})
    .filter(([, n]) => n > 0)
    .sort((a, b) => b[1] - a[1])
    .map(([code, value]) => ({ label: INTAKE_CHANNEL_LABEL[code] ?? humanise(code), value }))

  // Fixed order, and every bucket present even at zero: the shape of the wait is the
  // information, and a missing "15+" reads as "none" only if the reader can see the slot.
  const AGE_ORDER = ['0-3', '4-7', '8-14', '15+']
  const ageBuckets = stats.openAgeBuckets
    ? AGE_ORDER.map((k) => ({
        label: k === '15+' ? '15+ days' : `${k} days`,
        value: stats.openAgeBuckets?.[k] ?? 0,
      }))
    : null

  const raised = (stats.raisedDaily ?? []).map((d) => ({ label: shortDay(d.date), value: d.count }))
  const recorded = (stats.recordedDaily ?? []).map((d) => ({ label: shortDay(d.date), value: d.count }))
  // Non-zero, not non-empty: the series is zero-filled by design, so `length > 0` is
  // true even for a filter that matches nothing, and two flat empty panels would
  // replace a breakdown that still has something to say.
  const trend = raised.some((p) => p.value > 0) || recorded.some((p) => p.value > 0)

  const kinds = stats.byKind
  const transfers = kinds?.TRANSFER ?? 0
  const registrations = kinds?.REGISTRATION ?? 0
  const kindTotal = transfers + registrations

  // A status dot beside a bar's label is the one place state may be coloured, and only
  // because it comes with the word next to it.
  const statusTone = (label: string) => {
    const code = Object.keys(TRANSFER_STATUS_LABEL)
      .find((c) => TRANSFER_STATUS_LABEL[c] === label)
    const tone = code ? STATUS_TONE[code] : undefined
    return tone === 'open' ? 'warn' : tone
  }

  return (
    <div className="grid gap-3 lg:grid-cols-2">
      <Panel>
        <div className="space-y-4">
          {ageBuckets && (
            <ChartFrame
              title="How long open requests have waited"
              description="Days in their current state. The right-hand bars are somebody's morning."
            >
              <RankedBars points={ageBuckets} emptyMessage="Nothing open." />
            </ChartFrame>
          )}

          {kindTotal > 0 && (
            <ChartFrame title="What the queue is made of">
              <KindSplit transfers={transfers} registrations={registrations} />
            </ChartFrame>
          )}

          <ChartFrame title="Where requests are" description="Every status with at least one request">
            <RankedBars points={byStatus} tone={statusTone} emptyMessage="No requests in scope." />
          </ChartFrame>
        </div>
      </Panel>

      <Panel>
        <div className="space-y-4">
          {trend && (
            <>
              <ChartFrame title="Raised" description="Requests opened, by day">
                <Sparkbars points={raised} height={72} />
                <BucketAxis points={raised} />
              </ChartFrame>
              <ChartFrame title="Recorded" description="Confirmed by the IEBC and written to our register">
                <Sparkbars points={recorded} height={72} emptyMessage="Nothing recorded in this window." />
                <BucketAxis points={recorded} />
              </ChartFrame>
            </>
          )}

          <ChartFrame title="Why they were raised" description="Top six reasons">
            <RankedBars points={byReason} emptyMessage="No requests in scope." />
          </ChartFrame>

          <ChartFrame title="How they reached us" description="Intake channel">
            <RankedBars points={byChannel} emptyMessage="No requests in scope." />
          </ChartFrame>
        </div>
      </Panel>
    </div>
  )
}

/**
 * Two parts of one whole, as one bar.
 *
 * Two categories is the one case where part-to-whole beats ranked bars — the reader's
 * question is "how much of this is registration work", which is a share, not a ranking.
 * Identity is carried by the labels either side; the two fills are two steps of the SAME
 * hue, so nobody has to tell two colours apart to read it.
 */
function KindSplit({ transfers, registrations }: { transfers: number; registrations: number }) {
  const total = transfers + registrations
  const pct = (n: number) => (total === 0 ? 0 : (n / total) * 100)

  return (
    <div>
      <div className="flex h-5 w-full overflow-hidden rounded-md" role="img"
           aria-label={`${transfers} transfers and ${registrations} registrations`}>
        <div className="bg-chocolate h-full" style={{ width: `${pct(transfers)}%` }} />
        {/* 2px of surface between the segments, so the boundary is a gap and not a
            colour change that has to be perceived. */}
        <div className="w-0.5 shrink-0 bg-card" />
        <div className="bg-chocolate/35 h-full" style={{ width: `${pct(registrations)}%` }} />
      </div>
      <div className="flex justify-between mt-1.5 text-erp-xs">
        <span>
          <span className="font-medium">{fmtNumber(transfers)}</span>
          <span className="text-muted-foreground"> transfers</span>
        </span>
        <span>
          <span className="font-medium">{fmtNumber(registrations)}</span>
          <span className="text-muted-foreground"> registrations</span>
        </span>
      </div>
    </div>
  )
}

/** `2026-08-14` → `14 Aug`. The axis shows three of these, so they must be short. */
function shortDay(iso: string) {
  const d = new Date(`${iso}T00:00:00`)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString('en-KE', { day: 'numeric', month: 'short' })
}
